import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import { getPlatformSettings, updatePlatformSettings } from "@/lib/admin";
import { apiDownload } from "@/lib/api";

export default function AdminConfiguracionPage() {
  const { t } = useTranslation("admin");
  const [platformName, setPlatformName] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [commissionPct, setCommissionPct] = useState("25");
  const [currency, setCurrency] = useState("MXN");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPlatformSettings()
      .then((s) => {
        if (cancelled) return;
        setPlatformName(s.platformName);
        setSupportEmail(s.supportEmail);
        setCommissionPct((s.defaultCommissionRate * 100).toString());
        setCurrency(s.defaultCurrency);
      })
      .catch(() => { /* defaults */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    const pct = Number(commissionPct);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) { setError(t("admin.cfg.commissionRange")); return; }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updatePlatformSettings({
        platformName: platformName.trim(),
        supportEmail: supportEmail.trim(),
        defaultCommissionRate: pct / 100,
        defaultCurrency: currency,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("admin.cfg.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const exportDatabase = async () => {
    setExporting(true);
    setExportMsg(null);
    try {
      const { blob, filename } = await apiDownload("/admin/database/export");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportMsg({ kind: "ok", text: t("admin.cfg.dbExportDone") });
      setTimeout(() => setExportMsg(null), 4000);
    } catch (e) {
      setExportMsg({ kind: "err", text: e instanceof Error ? e.message : t("admin.cfg.dbExportError") });
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title={t("admin.cfg.title")}>
      <div className="max-w-2xl space-y-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
          <h2 className="text-base font-bold text-charcoal mb-4">{t("admin.cfg.general")}</h2>
          {loading ? (
            <div className="py-6 text-center text-gray-400 text-sm">{t("admin.loading")}</div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1.5">{t("admin.cfg.platformName")}</label>
                <input type="text" value={platformName} onChange={(e) => setPlatformName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean" />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1.5">{t("admin.cfg.supportEmail")}</label>
                <input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1.5">{t("admin.cfg.defaultCommission")}</label>
                  <input type="number" min={0} max={100} step={0.5} value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean" />
                  <p className="text-xs text-gray-400 mt-1">{t("admin.cfg.commissionHint")}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1.5">{t("admin.cfg.currency")}</label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean bg-white">
                    <option value="MXN">{t("admin.cfg.currencyMxn")}</option>
                    <option value="USD">{t("admin.cfg.currencyUsd")}</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          {error && <span className="text-xs text-coral">{error}</span>}
          {saved && <span className="text-xs text-emerald-600">{t("admin.saved")}</span>}
          <button onClick={save} disabled={saving || loading} className="inline-flex items-center gap-2 bg-ocean text-white text-sm font-medium px-6 py-2.5 rounded-full hover:bg-ocean/90 disabled:opacity-50">
            <i className="ri-save-line" /> {saving ? t("admin.saving") : t("admin.save")}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
          <h2 className="text-base font-bold text-charcoal mb-1.5">{t("admin.cfg.dbTitle")}</h2>
          <p className="text-sm text-gray-500 mb-4">{t("admin.cfg.dbDesc")}</p>
          <div className="flex items-center gap-3">
            <button
              onClick={exportDatabase}
              disabled={exporting}
              className="inline-flex items-center gap-2 border border-ocean text-ocean text-sm font-medium px-5 py-2.5 rounded-full hover:bg-ocean/5 disabled:opacity-50"
            >
              <i className={exporting ? "ri-loader-4-line animate-spin" : "ri-database-2-line"} />
              {exporting ? t("admin.cfg.dbExporting") : t("admin.cfg.dbExport")}
            </button>
            {exportMsg && (
              <span className={`text-xs ${exportMsg.kind === "ok" ? "text-emerald-600" : "text-coral"}`}>
                {exportMsg.text}
              </span>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
